// Calmasutras landing-page copy variants for A/B testing.
//
// `default`  - the existing production copy (unchanged).
// `v2`       - rewired copy (Calmasutras_Copy_V2). Targets peak-performing
//              CEOs, CMOs, founders, designers, writers.
//
// Add new variants here. Any key missing from a non-default variant falls back
// to the `default` value via `pickCopy()` in lib/copyVariant.jsx.

export const VARIANT_DEFAULT = "default";
export const VARIANT_V2 = "v2";

export const ACTIVE_VARIANTS = [VARIANT_DEFAULT, VARIANT_V2];

const defaultCopy = {
  // ----- HERO -----
  heroMarquee:
    "A GAME OF CHOICE WITHIN THE GAME OF CHANCE \u2022 52 SUTRAS \u2022 CALMNESS IS NOW JUST A GAME AWAY \u2022",
  heroSubline:
    "52 playing cards. 52 sutras of wisdom. A transformative journey of calm.",
  heroPill1: "52 Cards",
  heroPill2: "4 Suits",
  heroPill3: "1 Journey",
  heroPrimaryCta: "Buy eBook",
  heroSecondaryCta: "Buy the deck",

  // ----- SHOWCASE / THE DECK -----
  deckEyebrow: "The Deck",
  deckHeading1: "52 Cards. 52 Sutras.",
  deckHeading2: "One Transformative Journey.",
  deckBody:
    "Each card represents a unique sutra, a step in the journey of a playful monkey who gains profound insights as they progress. An innovative, immersive experience of wisdom and enlightenment.",

  // Card showcase (sticky stack)
  showcaseEyebrow: "Card Showcase",
  showcaseHeadingLine1: "Each card",
  showcaseHeadingLine2: "tells a story.",
  showcaseSideBody:
    "52 hand-illustrated cards. Each one carries a single sutra, a small idea you can pull, read, and carry into your day.",

  // ----- SUTRA OVERRIDES -----
  // Override per-sutra-id descriptions only when present.
  sutraOverrides: {}, // shape: { [sutraId]: "rewired description" }

  // ----- CARD OF DAY -----
  // Per-sutra description overrides (only used by Card of Day if matched).
  cardOfDayOverrides: {},

  // ----- BEGIN YOUR JOURNEY / PRICING -----
  journeyEyebrow: "Begin Your Journey",
  journeyHeadingPrefix: "Calmness,",
  journeyRotatingWords: [
    "just a card away.",
    "just a draw away.",
    "just a breath away.",
    "just a sutra away.",
    "just a moment away.",
  ],
  journeySubtext: "Pick the path that fits. Read first, hold the cards, or do both.",

  // eBook
  productEbookBadge: "Digital Read",
  productEbookTitle: "The eBook",
  productEbookSub: "The full 52 ideas, by Rajesh Setty. Read anywhere.",
  productEbookIncludes: [
    "All 52 chapters",
    "PDF, read on any device",
    "Lifetime download access",
  ],
  productEbookCta: "Buy the eBook",

  // Bundle
  productBundleBadge: "Deck + eBook",
  productBundleTitle: "The Bundle",
  productBundleSub: "Both worlds, at a better price.",
  productBundleIncludes: [
    "The full Calmasutras deck (52 cards)",
    "The complete eBook (PDF)",
    "$5 flat shipping (free with referral)",
    "You save $5.99",
  ],
  productBundleCta: "Get the Bundle",

  // Deck
  productDeckBadge: "Physical Cards",
  productDeckTitle: "The Deck",
  productDeckSub: "52 hand-illustrated cards. Hold the wisdom in your hands.",
  productDeckIncludes: [
    "52 hand-illustrated cards",
    "Premium tuck box",
    "$5 flat shipping (free with referral code)",
  ],
  productDeckCta: "Buy Calmasutras deck",

  // 2+ deck callout (DeckCard)
  bulkCalloutEyebrow: "Save with 2+ decks",

  // ----- GIFTING BANNER ON HOME -----
  giftBannerEyebrow: "Calmasutras Gifting",
  giftBannerHeadline: "Gifting Calmasutras to your team, clients, or vendors?",
  giftBannerBody:
    "Bulk pricing from $25/deck \u00b7 free shipping at 10+ \u00b7 individual recipient delivery via private link.",
  giftBannerCta: "Explore",

  // ----- GIFT LANDING SECTION (big section near footer) -----
  giftSectionEyebrow: "The Calmasutras Gift",
  giftSectionHeadlineL1: "Most gifts are opened once.",
  giftSectionHeadlineL2: "This one is opened a little, every day.",
  giftSectionBody:
    "52 hand-illustrated cards of stillness, sent to your team, your clients, or someone you love. Each card returns a small moment of calm, week after week, all year long.",

  // ----- COMPANION BOOK / SUTRAS -----
  sutrasEyebrow: "The Companion Book",
  sutrasHeadingL1: "52 Ideas to Stay Calm",
  sutrasHeadingL2: "While Accomplishing More",
  sutrasContextNote: "", // optional one-line context (empty in default)

  // ----- NOT JUST A DECK -----
  notJustHeadingL1: "Not just a deck of cards.",
  notJustHeadingL2: "A deck of wisdom.",
  notJustBody:
    "52 hand-illustrated cards. A companion eBook. Or both, at a better price.",
  notJustCta1: "Get the Bundle",
  notJustCta2: "Buy Calmasutras deck",

  // ----- GALLERY -----
  galleryEyebrow: "Glimpse the Gallery",
  galleryHeading: "A Peek into Every Suit",
  galleryBody: "A taste of each suit. Get the deck to hold all 52 in your hand.",
  galleryBottomEyebrow: "Want all 52?",
  galleryBottomTitle: "Bring the full deck home.",
  galleryBottomBody: "52 hand-illustrated cards \u00b7 ships in 3-5 days",

  // ----- PUZZLE / HIDDEN SURPRISE -----
  puzzleEyebrow: "A hidden surprise",
  puzzleHeadingL1: "Not just a deck.",
  puzzleHeadingL2: "A puzzle that grows a tree.",
  puzzleBody:
    "Open the box. Read a card. Then, when you have a quiet hour, lay all 52 out together. They click into a single, hand-painted Calm Tree. A meditation you can build with your hands, your kids, or a friend on a slow Sunday afternoon.",
  puzzleFeature1Title: "It's a puzzle, too.",
  puzzleFeature1Body:
    "Lay all 52 cards out together and a single Calm Tree comes alive, every card a leaf.",
  puzzleFeature2Title: "Family ritual.",
  puzzleFeature2Body:
    "Quiet weekend afternoons. Tea, conversation, kids gathering pieces. A slow, shared kind of joy.",
  puzzleFeature3Title: "More than a deck.",
  puzzleFeature3Body:
    "Pull one card for daily wisdom. Or assemble all 52, and watch a forest grow on your table.",
  puzzleCta: "Buy the deck \u00b7 $29",

  // ----- LIFESTYLE / IN THE WILD -----
  lifestyleEyebrow: "In the Wild",
  lifestyleHeadingL1: "Not for the shelf.",
  lifestyleHeadingL2: "For the table.",
  lifestyleBody:
    "Calmasutras is a deck you actually use. Friends over tea. A long Sunday. Quiet afternoons that turn into conversations. Here is what that looks like.",

  // ----- FAQ -----
  faqEyebrow: "Frequently \u00b7 Curiously \u00b7 Asked",
  faqHeadingL1: "Questions, answered.",
  faqHeadingL2: "Honestly. Briefly.",
  faqFooter: "Still wondering?",
  faqFooterCta: "Talk to us \u2192",
  faqItems: [
    {
      q: "Is this self-help in disguise?",
      a: "Self-help with disguise. Big difference. You can read it like a book, play with it like a deck, or leave it on your desk like the conversation piece it secretly is.",
    },
    {
      q: "Can I play actual card games with it?",
      a: "Yes. It is a real, fully-functional 52-card deck. Bridge, rummy, gin, poker, the game your grandfather invented \u2014 all on the table. The sutras stay quiet until someone flips a card and reads one out loud.",
    },
    {
      q: "Is it religious or spiritual?",
      a: "Neither. Sutra just means \"thread\" in Sanskrit. Ours threads through 52 ideas about working, leading, and not losing your mind on a Tuesday.",
    },
    {
      q: "Will I become enlightened?",
      a: "Almost certainly not. You will, however, probably make one or two slightly better decisions this month. Which is the kind of enlightenment we can deliver in a tuck box.",
    },
    {
      q: "What if I already own seventeen decks of cards?",
      a: "Then you already understand. This is the eighteenth.",
    },
    {
      q: "What is actually inside the box?",
      a: "52 hand-illustrated playing cards. A short book on every idea. A hidden picture-puzzle inside the 52. One nicer-than-it-needs-to-be tuck box. No batteries. No subscription. No app.",
    },
    {
      q: "Can I gift it without making it weird?",
      a: "It is the least weird gift on your list. Nobody has ever said \"a thoughtfully designed card deck made me uncomfortable.\" Bulk orders ship directly to each person via a private link \u2014 nothing for you to coordinate.",
    },
    {
      q: "When does it ship?",
      a: "Shipping now. Every order goes out within 3 business days, worldwide. We email you the day your deck is on a truck.",
    },
    {
      q: "What if I just do not get it?",
      a: "Then we will happily take it back. Email us within 30 days of arrival, send the deck home, get a refund. Calmness, restored.",
    },
  ],
};

const v2Copy = {
  // ----- HERO -----
  heroMarquee:
    "A COOL WAY TO LET GO OF STRESS \u2022 A BOOK FOLDED INTO 52 CARDS \u2022 DRAWN, NOT MASS-PRODUCED \u2022 BUILT TO STAY ON YOUR DESK \u2022",
  heroSubline:
    "A cool way to let go of stress. A short book folded into a deck of 52 cards, drawn by hand.",
  heroPill1: "52 Cards",
  heroPill2: "1 Book",
  heroPill3: "1 Hidden Puzzle",
  heroPrimaryCta: "Read the Ideas",
  heroSecondaryCta: "Buy the Deck",

  // ----- SHOWCASE / THE DECK -----
  deckHeading1: "52 cards. 1 book.",
  deckHeading2: "Folded into each other.",
  deckBody:
    "Calmasutras is a playing-card deck with a small book tucked inside the suits. Each card carries one idea, distilled across three decades of Rajesh Setty's life as a tech founder, mentor, and writer. Drawn by hand. Quietly useful. The kind of thing that earns its spot on your desk.",

  // Card showcase
  showcaseHeadingLine1: "Each card,",
  showcaseHeadingLine2: "a keeper.",
  showcaseSideBody:
    "52 cards, drawn one at a time. Each holds a single idea \u2014 short enough to read in a minute, considered enough to sit with all afternoon. A whole book quietly folded into a deck you will pick up more often than you expect.",

  // Featured sutra rewrites - present the ideas with reverence, not pain language
  sutraOverrides: {
    1: "An old idea, given a new home. You can take care of others only as well as you take care of yourself \u2014 and not a step further. The oxygen mask first. The simplest principle in leadership, hidden in plain sight.",
    3: "Two labels. That is all. Every encounter, every outcome, every conversation \u2014 a gift or a lesson, nothing else. The moment you label it, you stop carrying it. An idea older than every productivity system, and still the most useful.",
    13: "There is curiosity that builds you, and curiosity that drains you. The skill is in knowing the difference before you click. A simple sorting question that quietly returns the hours you never realised you were spending.",
    2: "From the Bhagavad Gita to your Monday morning. You can grieve outcomes that have not happened, or you can be free of them. The same idea, told a thousand ways across the centuries \u2014 captured here in one card.",
  },

  // Card of the Day - Sutra 6 ("Serious work, light self")
  cardOfDayOverrides: {
    6: "Take the work seriously. Take yourself a little less so. The lighter you carry it, the better the work gets. An old idea from a master of staying calm. Still working, every day, decades later.",
  },

  // ----- BEGIN YOUR JOURNEY / PRICING -----
  journeyEyebrow: "Begin",
  journeyHeadingPrefix: "Calmness,",
  journeyRotatingWords: [
    "a card at a time.",
    "a moment at a time.",
    "a habit at a time.",
    "beautifully by design.",
  ],
  journeySubtext:
    "Read first. Hold the deck. Or both. The format you choose decides the ritual.",

  // eBook
  productEbookBadge: "Digital Read",
  productEbookSub:
    "Rajesh Setty's complete book \u2014 52 ideas, three decades of thinking, all in one place. Read it in one sitting. Return to the ideas that hit.",
  productEbookIncludes: [
    "All 52 ideas, in full",
    "Read on any device",
    "Yours, permanently",
  ],
  productEbookCta: "Read the Ideas",

  // Bundle
  productBundleBadge: "Deck + eBook",
  productBundleSub:
    "Read the book. Hold the deck. The full thing \u2014 the cooler way to keep an idea close.",
  productBundleIncludes: [
    "The complete 52-card deck",
    "The full eBook with every idea",
    "$5 flat shipping (free with referral)",
    "You save $5.99",
  ],
  productBundleCta: "Get the Bundle",

  // Deck
  productDeckBadge: "Physical Cards",
  productDeckSub:
    "52 cards, drawn by hand, in a tuck box that looks better than it has any reason to. Pulls clean. Sits well on a desk. Carries the whole book between its suits.",
  productDeckIncludes: [
    "52 hand-drawn playing cards",
    "Premium tuck box",
    "$5 flat shipping (free with your referral)",
  ],
  productDeckCta: "Buy the Deck",

  // 2+ callout
  bulkCalloutEyebrow: "Gifting it to your team?",

  // ----- GIFTING BANNER ON HOME -----
  giftBannerHeadline: "The thoughtful gift, designed to be kept.",
  giftBannerBody:
    "From $25 per deck in bulk. Free shipping at 10 or more. Each recipient gets theirs sent directly via a private link \u2014 no coordination at your end. Leadership offsites, client thank-yous, end-of-year hampers \u2014 the gift that doesn't get re-gifted.",
  giftBannerCta: "Explore Gifting Options",

  // ----- GIFT LANDING SECTION -----
  giftSectionHeadlineL1: "The thoughtful gift.",
  giftSectionHeadlineL2: "Built to be kept.",
  giftSectionBody:
    "Drawn by hand, with a small book between the suits and a picture-puzzle hiding inside the 52. Each recipient gets theirs delivered directly. Leaders, teams, clients, offsite guests \u2014 the kind of gift that survives the drawer test.",

  // ----- COMPANION BOOK -----
  sutrasContextNote:
    "A book disguised as a deck. Written by Rajesh Setty across three decades of building, mentoring, and writing about what calm under pressure actually looks like.",

  // ----- NOT JUST A DECK -----
  notJustHeadingL1: "Not just a deck of cards.",
  notJustHeadingL2: "A book in beautiful disguise.",
  notJustBody:
    "A short book by Rajesh Setty, broken into 52 cards. A picture-puzzle hiding inside them. Drawn by hand. Quietly useful. The object you reach for without thinking about why.",
  notJustCta1: "Get the Bundle",
  notJustCta2: "Buy the Deck",

  // ----- GALLERY -----
  galleryHeading: "52 little worlds, drawn one at a time.",
  galleryBody:
    "Each suit takes a different angle \u2014 how you think, how you lead, how you recover, how you choose. Every card drawn by hand. Browse slowly.",
  galleryBottomTitle: "The whole deck. Every idea. Yours.",
  galleryBottomBody: "52 hand-drawn cards \u00b7 free shipping \u00b7 ships in 3 to 5 days",

  // ----- PUZZLE -----
  puzzleBody:
    "Open the box. Pull a card each morning. Then one quiet afternoon, lay all 52 out on the table. They click into a single hand-painted Calm Tree \u2014 every card a leaf, every idea a branch. A puzzle you did not know you bought. The kind of afternoon you keep promising yourself.",
  puzzleFeature1Title: "A puzzle hidden inside the deck.",
  puzzleFeature1Body:
    "Lay all 52 out and the Calm Tree appears. Every card a leaf. Every idea a branch. A hidden picture quietly built into the deck.",
  puzzleFeature2Title: "A ritual worth protecting.",
  puzzleFeature2Body:
    "Tea. No screens. Kids passing pieces across the table. The kind of afternoon that becomes the one they remember.",
  puzzleFeature3Title: "Two practices, one deck.",
  puzzleFeature3Body:
    "One card every morning for a quiet daily edge. Or all 52 together to build something people stop mid-conversation to ask about.",
  puzzleCta: "Buy the deck \u00b7 $29",

  // ----- LIFESTYLE / IN THE WILD (V2 voice) -----
  lifestyleEyebrow: "In the Wild",
  lifestyleHeadingL1: "It travels well.",
  lifestyleHeadingL2: "It plays well too.",
  lifestyleBody:
    "Tea on a stone table. A long lunch with someone you actually like. A Sunday that refuses to be hurried. Calmasutras was made for moments like these \u2014 and the people who collect them.",

  // ----- FAQ (V2 voice) -----
  faqEyebrow: "Reasonable Questions",
  faqHeadingL1: "You probably wondered.",
  faqHeadingL2: "Here is the truth.",
  faqFooter: "Question we did not answer?",
  faqFooterCta: "Ask us anything \u2192",
  faqItems: [
    {
      q: "Wait \u2014 is this self-help in a tuck box?",
      a: "Self-help in disguise. There is a difference. Read it like a book. Shuffle it like a deck. Or just leave it sitting on the desk and pretend you bought it for the cover art. All three work.",
    },
    {
      q: "So can I actually play games with it?",
      a: "Of course. It is a real 52-card deck. Bridge, rummy, gin, poker, the made-up game your cousin insists on \u2014 all fair play. The ideas wait quietly until somebody flips a card and decides to read it out loud. Then dinner gets interesting.",
    },
    {
      q: "Is this a spiritual thing? Be honest.",
      a: "It is honest. Sutra means \"thread\" in Sanskrit. Nothing more mystical than that. Ours just happens to thread through 52 useful ideas about leading, building, and surviving a Tuesday afternoon.",
    },
    {
      q: "Will it make me enlightened, focused, and impossibly serene?",
      a: "Almost certainly not. It will, however, probably help you make one or two slightly better calls this month than you would have otherwise. Which is roughly all the enlightenment we feel comfortable promising in writing.",
    },
    {
      q: "I already own a dozen decks of cards. Why this one?",
      a: "Because none of the other twelve also contain a book and a hidden puzzle. This one quietly does the work of three objects while taking up the room of one.",
    },
    {
      q: "What exactly comes in the box?",
      a: "52 hand-illustrated playing cards. A short book of ideas, one per card. A picture-puzzle hidden across all 52. A tuck box that is nicer than it strictly needs to be. No batteries. No subscription. No app. No notifications. We checked.",
    },
    {
      q: "Can I send it as a corporate gift without it being awkward?",
      a: "It is the gift that does not get re-gifted. We ship directly to each recipient via a private link \u2014 you do not lift a finger, and nobody has to lug 47 decks home from an offsite. Free shipping kicks in at 10 decks.",
    },
    {
      q: "When can I actually hold one?",
      a: "In stock and shipping now. Orders leave the warehouse within 3 business days, and we email you the moment your deck is on a truck.",
    },
    {
      q: "And if I open it and it just is not for me?",
      a: "Then it goes home and your money comes back. Email us within 30 days of arrival, ship the deck back, get a full refund. No survey. No \"are you sure?\" form. Calmness, restored.",
    },
  ],
};

export const COPY_VARIANTS = {
  [VARIANT_DEFAULT]: defaultCopy,
  [VARIANT_V2]: v2Copy,
};

// Resolve a single key, falling back to `default` if the variant doesn't override it.
export function resolveCopy(variant, key) {
  const variantMap = COPY_VARIANTS[variant] || COPY_VARIANTS[VARIANT_DEFAULT];
  if (Object.prototype.hasOwnProperty.call(variantMap, key)) return variantMap[key];
  return COPY_VARIANTS[VARIANT_DEFAULT][key];
}
